import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, ButtonBase } from '@mui/material'
import { Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { StackItem } from 'ui/layout/StackItem'
import Section from 'ui/layout/Section'
import { reformatBirthday } from 'ui/utils/dateFormatUtils'
import { ThumbnailList } from 'ui/utils/Thumbnails'
import type { ReservationDetailProps } from 'types/med'
import { Text1 } from 'ui/shorthands/Texts'
import Modal from 'ui/layout/Modal'
import ChangeAddressModalContent, {
  ChangeAddressModalContentProps,
} from 'ui/partials/ChangeAddressModalContent'
import theme from 'ui/theme'
import { FormToSmartOneHealth } from './FormToSmartOneHealth'

const useStyles = makeStyles()(() => ({
  bothSide: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  kana: {
    fontSize: 12,
    lineHeight: 1,
  },
  phrButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  phrButton: {
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  phrButtonDisabled: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
}))

export type ShippingProps = {
  onChangeAddress: ChangeAddressModalContentProps['onSubmit']
}

export type PhrIntegrationProps = {
  onClickPhrIntegrationButton: () => Promise<{ token: string }>
}

export type ProfileSectionProps = {
  reservation: ReservationDetailProps
} & ShippingProps &
  PhrIntegrationProps

const ProfileSection: React.FC<ProfileSectionProps> = ({
  reservation,
  onChangeAddress,
  onClickPhrIntegrationButton,
}) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const [showChangeAddressModal, seShowChangeAddressModal] =
    useState<boolean>(false)
  const [showPhrIntegrationModal, setShowPhrIntegrationModal] =
    useState<boolean>(false)
  const [phrIntegrationLoading, setPhrIntegrationLoading] =
    useState<boolean>(false)
  const [phrIntegrationToken, setPhrIntegrationToken] = useState<string | null>(
    null
  )
  const [phrIntegrationErrorMessage, setPhrIntegrationErrorMessage] =
    useState<React.ReactNode | null>(null)

  const resetPhrIntegrationState = () => {
    setPhrIntegrationToken(null)
    setPhrIntegrationErrorMessage(null)
    setPhrIntegrationLoading(false)
    setShowPhrIntegrationModal(false)
  }

  useEffect(() => {
    resetPhrIntegrationState()
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [reservation.id, reservation.is_phr_integration])

  useEffect(() => {
    if (showPhrIntegrationModal) {
      const phrModalTimer = setTimeout(() => {
        resetPhrIntegrationState()
      }, 9 * 60 * 1000)
      return () => {
        resetPhrIntegrationState()
        clearTimeout(phrModalTimer)
      }
    }
  }, [showPhrIntegrationModal])

  return (
    <Section>
      <StackItem label={t('氏名')}>
        <Box className={classes.nameContainer}>
          {reservation?.applicant_last_name_kana && (
            <Text1 className={classes.kana}>
              {reservation.applicant_last_name_kana}{' '}
              {reservation?.applicant_first_name_kana}
            </Text1>
          )}
          <Text1>
            {reservation.applicant_last_name} {reservation.applicant_first_name}
          </Text1>
        </Box>
      </StackItem>
      {reservation.is_phr_integration && reservation.soh_access_url && (
        <StackItem label={t('PHRデータ連携')} alignTop>
          <ButtonBase
            className={cx(
              classes.phrButton,
              phrIntegrationLoading && classes.phrButtonDisabled
            )}
            disabled={phrIntegrationLoading}
            onClick={() => {
              setShowPhrIntegrationModal(true)
              setPhrIntegrationLoading(true)
              return onClickPhrIntegrationButton()
                .then(({ token }) => {
                  setPhrIntegrationToken(token)
                  setPhrIntegrationErrorMessage(null)
                })
                .catch(() => {
                  setPhrIntegrationToken(null)
                  setPhrIntegrationErrorMessage(
                    <Trans i18nKey="trans.連携先のサービスでエラーが発生しました。..." />
                  )
                })
                .finally(() => setPhrIntegrationLoading(false))
            }}
          >
            {t('Smart One Healthを開く')}
          </ButtonBase>
          <Modal
            open={showPhrIntegrationModal}
            handleClose={resetPhrIntegrationState}
          >
            <FormToSmartOneHealth
              url={reservation.soh_access_url}
              token={phrIntegrationToken}
              isLoading={phrIntegrationLoading}
              errorMessage={phrIntegrationErrorMessage}
              handleClose={resetPhrIntegrationState}
            />
          </Modal>
        </StackItem>
      )}
      <StackItem label={t('生年月日')}>
        <Text1>
          {reservation.applicant_birthday &&
            reformatBirthday(reservation.applicant_birthday)}
        </Text1>
      </StackItem>
      <StackItem label={t('電話番号')}>
        <Text1>
          {reservation.applicant_phone && (
            <Link href={`tel:${reservation.applicant_phone}`}>
              {reservation.applicant_phone}
            </Link>
          )}
        </Text1>
      </StackItem>
      {reservation.applicant_email && (
        <StackItem label={t('メールアドレス')}>
          <Text1>
            <Link href={`mailto:${reservation.applicant_email}`}>
              {reservation.applicant_email}
            </Link>
          </Text1>
        </StackItem>
      )}
      <StackItem label={t('住所')}>
        <div className={classes.bothSide}>
          <Text1>
            {reservation.applicant_postal_code}&nbsp;
            {reservation.applicant_address}
          </Text1>
          {reservation.how_to_receive_medicine &&
            reservation.how_to_receive_medicine === 'delivery' && (
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => seShowChangeAddressModal(true)}
              >
                {t('編集')}
              </Button>
            )}
          <Modal
            large
            open={showChangeAddressModal}
            handleClose={() => seShowChangeAddressModal(false)}
          >
            <ChangeAddressModalContent
              onSubmit={(data) => {
                return onChangeAddress(data).finally(() =>
                  seShowChangeAddressModal(false)
                )
              }}
              onClose={() => seShowChangeAddressModal(false)}
              defaultValues={{
                applicant_address: reservation.applicant_address || '',
                applicant_postal_code: reservation.applicant_postal_code || '',
              }}
            />
          </Modal>
        </div>
      </StackItem>
      <StackItem label={t('診察券番号')}>
        <Text1>{reservation.registration_card_number}</Text1>
      </StackItem>

      {!!reservation.online_eligibility_verification_agreed && (
        <StackItem label={t('マイナ保険証')} alignTop>
          <Text1>{t('同意済み')}</Text1>
        </StackItem>
      )}
      {!reservation.online_eligibility_verification_agreed &&
        reservation.insurance_card_images && (
          <StackItem label={t('保険資格情報')} alignTop>
            <ThumbnailList images={reservation.insurance_card_images} />
          </StackItem>
        )}

      {reservation.certificate_images && (
        <StackItem label={t('その他証書')} alignTop>
          <ThumbnailList images={reservation.certificate_images} />
        </StackItem>
      )}

      {reservation.identity_verification_images && (
        <StackItem label={t('本人確認書類')} alignTop>
          <ThumbnailList images={reservation.identity_verification_images} />
        </StackItem>
      )}
    </Section>
  )
}

export default ProfileSection
